export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from?: number;
  to?: number;
}

interface PaginationProps {
  meta: PaginationMeta;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  perPageOptions?: number[];
}

export function Pagination({
  meta,
  onPageChange,
  onPerPageChange,
  perPageOptions = [10, 20, 50],
}: PaginationProps) {
  const { current_page, last_page, total, per_page, from, to } = meta;

  if (total === 0) return null;

  const pages = buildPageNumbers(current_page, last_page);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100">
      <p className="text-xs text-slate-500">
        {from && to ? `${from}–${to} sur ${total}` : `${total} résultat${total > 1 ? 's' : ''}`}
      </p>

      <div className="flex items-center gap-2">
        {onPerPageChange && (
          <select
            value={per_page}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600"
          >
            {perPageOptions.map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
        )}

        <button
          disabled={current_page <= 1}
          onClick={() => onPageChange(current_page - 1)}
          className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-left-s-line" />
        </button>

        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="px-1 text-slate-400 text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p as number)}
              className={`min-w-[32px] h-8 rounded-lg text-xs font-semibold ${
                p === current_page
                  ? 'bg-teal-600 text-white'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {p}
            </button>
          ),
        )}

        <button
          disabled={current_page >= last_page}
          onClick={() => onPageChange(current_page + 1)}
          className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>
    </div>
  );
}

function buildPageNumbers(current: number, last: number): (number | '…')[] {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  if (current > 3) pages.push('…');
  for (let i = Math.max(2, current - 1); i <= Math.min(last - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < last - 2) pages.push('…');
  pages.push(last);
  return pages;
}
